import { useEffect, useRef, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { errorMessage } from "@/lib/errorMessage";
import type {
  EngagementScoringSettingsHistoryResponse,
  EngagementScoringSettingsRequest,
  EngagementScoringSettingsResponse,
  EngagementSettingsPreviewResponse,
} from "@/types/generated/cms.generated";

/**
 * The engagement score formula (CMS API `engagement-settings`; AthlynAdmin + AthlynTeam read and
 * preview, AthlynAdmin saves). One Athlyn-wide record; saved values apply at the next nightly run.
 */

const SETTINGS_KEY = ["engagement-settings"] as const;

/** Wait after the last edit before asking the server for a new preview. */
export const PREVIEW_DEBOUNCE_MS = 400;

export const SAVE_SUCCESS_TOAST = "Saved · applies tonight at 7:00 AM UAE";
const SAVE_ERROR_FALLBACK = "Couldn't save the formula. Please try again.";

export function useEngagementSettings() {
  return useQuery<EngagementScoringSettingsResponse>({
    queryKey: SETTINGS_KEY,
    queryFn: () => apiRequest<EngagementScoringSettingsResponse>("GET", "/engagement-settings"),
  });
}

/** Saved changes, newest first. */
export function useEngagementSettingsHistory() {
  return useQuery<EngagementScoringSettingsHistoryResponse[]>({
    queryKey: [...SETTINGS_KEY, "history"],
    queryFn: () => apiRequest<EngagementScoringSettingsHistoryResponse[]>("GET", "/engagement-settings/history"),
  });
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/**
 * What a candidate formula would do (example total, level changes, reach). Debounced; keeps the last
 * result while the next one loads. `body` is null and `enabled` false while the form is invalid — no
 * request is ever sent with an invalid body. The first (loaded) body is previewed straight away.
 */
export function useEngagementPreview(body: EngagementScoringSettingsRequest | null, enabled: boolean) {
  const hash = body && enabled ? JSON.stringify(body) : null;
  const debouncedHash = useDebouncedValue(hash, PREVIEW_DEBOUNCE_MS);
  const query = useQuery<EngagementSettingsPreviewResponse>({
    queryKey: [...SETTINGS_KEY, "preview", debouncedHash],
    queryFn: () =>
      apiRequest<EngagementSettingsPreviewResponse>(
        "POST",
        "/engagement-settings/preview",
        JSON.parse(debouncedHash as string) as EngagementScoringSettingsRequest
      ),
    enabled: enabled && hash !== null && debouncedHash !== null,
    placeholderData: keepPreviousData,
    // A refused body (4xx, e.g. a server-only rule) is not retried: its reason is shown at once.
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      return !(status !== undefined && status >= 400 && status < 500) && failureCount < 2;
    },
  });
  // "Current" = the answer belongs to the body on screen (not the debounced or a previous one).
  const forCurrentBody = hash !== null && debouncedHash === hash;
  // Refetching a failed query with no data resets it to "pending" and drops its error, so the last
  // failure is kept here: a Try again keeps the reason on screen (busy) instead of a skeleton.
  const lastFailure = useRef<{ hash: string; error: unknown } | null>(null);
  if (query.isError && debouncedHash !== null) lastFailure.current = { hash: debouncedHash, error: query.error };
  else if (query.isSuccess && !query.isPlaceholderData) lastFailure.current = null;
  const isRetrying = forCurrentBody && query.isFetching && !query.isError && lastFailure.current?.hash === hash;
  const isCurrentError = forCurrentBody && (query.isError || isRetrying);
  return {
    ...query,
    /** The preview of the body on screen has arrived (never a previous body's numbers). */
    isCurrent: forCurrentBody && query.isSuccess && !query.isPlaceholderData,
    /** The preview of the body on screen failed (also while Try again refetches it). */
    isCurrentError,
    /** Why it failed: the current error, or the last one while a Try again is in flight. */
    failure: isCurrentError ? (query.isError ? query.error : lastFailure.current?.error) : null,
    /** A Try again of the failed preview is in flight. */
    isRetrying,
  };
}

/**
 * The save-error text: the server's `errorDetails` when it is a non-empty string (validation
 * answers "Validation failed" as the message), else the error message, else the fallback.
 */
export function saveErrorMessage(error: unknown): string {
  return serverErrorMessage(error, SAVE_ERROR_FALLBACK);
}

/** Same rule for a failed preview (e.g. the server's "Consistency and Social can't both be 0% …"). */
export function previewErrorMessage(error: unknown): string {
  return serverErrorMessage(error, "Couldn't work out the preview.");
}

/** A server validation refusal (400): its reason is final, so no Try again is offered. */
export function isValidationError(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 400;
}

function serverErrorMessage(error: unknown, fallback: string): string {
  const details = (error as { errorDetails?: unknown } | null)?.errorDetails;
  if (typeof details === "string" && details.trim()) return details.trim();
  return errorMessage(error, fallback);
}

/** Saves the formula (AthlynAdmin). */
export function useSaveEngagementSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (body: EngagementScoringSettingsRequest) =>
      apiRequest<EngagementScoringSettingsResponse>("PUT", "/engagement-settings", body),
    // A failed save is never resent (the app default retries mutations once).
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      toast({ title: SAVE_SUCCESS_TOAST });
    },
    onError: (error: unknown) => {
      toast({ title: "Couldn't save the formula", description: saveErrorMessage(error), variant: "destructive" });
    },
  });
}
